import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { ReactNode, useEffect } from "react";
import { useImmer } from "use-immer";

import { IgGrayPrimitives } from "@intelligems/ig-design-tokens";
import {
  IgButton,
  IgCheckbox,
  IgIconButton,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  isShopifyPickQuestion,
  Option,
  SURVEY_DECLINED_MESSAGE,
  UserInputAnswer,
} from "@intelligems/ig-types";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material";

import { useOptionalPromptInputAttachments } from "@src/components/ai-elements/prompt-input-context/useOptionalPromptInputAttachments";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { ShopifyEntityCombobox } from "@src/features/ai/components/WorkflowEditor/execution/panel/ShopifyEntityCombobox";
import { ShopifyProductPickerDialog } from "@src/features/ai/components/WorkflowEditor/execution/panel/ShopifyProductPickerDialog";
import {
  shopifyEntityChooseLabel,
  ShopifyEntityPick,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/shopifyEntityPickerCopy";
import {
  deriveSurveyQuestions,
  surveyCardTitle,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/surveyAskState";
import { useIsTextRevealing } from "@src/features/ai/components/WorkflowEditor/execution/turn/AssistantMessage/useIsTextRevealing";

/**
 * The Figma "Survey card" (Atlas Component Library › Survey cards): how a
 * parked `request-user-input` question renders above the composer. One card
 * for every card-shaped ask — a legacy `choice` or `confirmation` is a single
 * question (no stepper, just the close X); a `survey` steps through its
 * questions with `‹ N of M ›` in the header.
 *
 * A `file` ask (Survey card › Select=File) is the same shell with no option
 * rows: the footer holds one full-width outlined "Choose file" button that
 * opens the composer's file dialog. Once a file is picked the card owns it —
 * the body lists every attachment as a removable chip and the footer button
 * becomes "Send to chat", which submits the composer's draft plus the uploaded
 * files (the panel stays disabled while an upload is still settling, so the
 * button only sends a file that is actually ready). The X declines exactly as
 * for a question.
 *
 * Layout is the Figma spec verbatim (theme spacing unit is 4px): a header
 * padded 12px at the sides, 12px above and 8px below, holding the question
 * (Body 2/Semi Bold) and the controls, then option rows inset 6px, each padded
 * 8px all round on a 6px radius with either a 16px checkbox or a 20px numbered
 * badge; the card is 12px-radius with the low shadow, and closes with 8px of
 * bottom padding unless a footer supplies its own 12px. The question ALWAYS renders in the header —
 * it is the card's title — but only up to about three lines: a longer prompt is
 * replaced by `SURVEY_CARD_TITLE_FALLBACK`, which points at the transcript
 * bubble that already shows the full text (see `surveyCardTitle`).
 *
 * A `shopify-pick` question renders its catalog inline (Figma "Atlas / Chat
 * combobox"): for collections, tags, vendors and product types the card body
 * IS the picker — a searchable, scrollable run of checkbox rows with Confirm
 * selections in the footer (see `ShopifyEntityCombobox`). Products keep the
 * Survey card › Select=Picker shape, since the shared product picker's images,
 * variants and filters have no inline equivalent: the footer button opens that
 * dialog, and once answered the body lists what was picked and the button
 * reopens it. Either way the answer carries ids for the agent and labels for
 * the transcript.
 *
 * Answering a single-select question is one click: the first click on a row
 * is the answer and the card auto-advances (or submits, on the last unanswered
 * question). Only a `multiSelect` question gets a Confirm selections button:
 * rows toggle, nothing sends until confirmation, and the reply lists every
 * picked option.
 *
 * Stepper rules (per the Figma spec): back (‹) shows on every question except
 * the first; forward (›) only once the next question is already answered —
 * never on the last — so nobody skips ahead. Hidden arrows keep their slot
 * reserved so the header never shifts. The X dismisses the card and resumes
 * the agent with a declined-to-answer message. Nothing renders while an
 * assistant bubble is still revealing itself, so the answer is never on offer
 * before the question has been shown.
 */
export const SurveyCard = () => {
  const theme = useTheme();
  const { inputRequirement, submitInput, onSubmit, survey, disabled } =
    useExecutionPanelContext();
  // The card answers the message that is still being painted; offering it
  // first lets a merchant answer a question they have not been shown.
  const revealing = useIsTextRevealing();

  const attachments = useOptionalPromptInputAttachments();

  const questions = deriveSurveyQuestions(inputRequirement);
  const currentIndex = survey?.currentIndex ?? 0;
  const question = questions?.[currentIndex];
  const existingAnswer = survey?.answers[currentIndex];

  // Multi-select assembly state for the CURRENT question. Re-seeded per
  // question (and per requirement) from any answer already recorded, so
  // stepping back shows the set that was picked — and a new question never
  // inherits the previous one's toggles.
  const [selected, setSelected] = useImmer<string[]>([]);
  useEffect(() => {
    setSelected(
      () => existingAnswer?.choices.map((choice) => choice.value) ?? []
    );
  }, [inputRequirement, currentIndex, existingAnswer, setSelected]);
  // Whether the current pick question's store picker is open. Closed again
  // whenever the question changes, so stepping away never leaves a dialog for
  // a question that is no longer on the card.
  const [pickerOpen, setPickerOpen] = useImmer(false);
  useEffect(() => {
    setPickerOpen(() => false);
  }, [inputRequirement, currentIndex, setPickerOpen]);

  if (revealing) {
    return null;
  }

  const declineAsk = () =>
    submitInput({ type: "text", content: SURVEY_DECLINED_MESSAGE });

  if (inputRequirement.userInput.type === "file") {
    const picked = attachments?.files ?? [];
    return (
      <SurveyCardShell
        title={surveyCardTitle(inputRequirement.prompt ?? "")}
        disabled={disabled}
        hasFooter
        onDismiss={declineAsk}
      >
        {picked.length > 0 && (
          <Stack
            data-testid="survey-card-files"
            direction="row"
            flexWrap="wrap"
            gap={1.5}
            sx={{ px: 3, py: 2 }}
          >
            {picked.map((file) => {
              const filename = file.filename ?? "(unnamed)";
              return (
                <Stack
                  key={file.id}
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{
                    backgroundColor:
                      theme.igBackgroundColors.interactive.neutral.default,
                    borderRadius: "6px",
                    maxWidth: "100%",
                    pl: 2,
                    pr: 1.5,
                    py: 1.5,
                  }}
                >
                  <IgTypography variant="body2" noWrap>
                    {filename}
                  </IgTypography>
                  <IgIconButton
                    aria-label={`Remove ${filename}`}
                    size="small"
                    disabled={disabled}
                    onClick={() => attachments?.remove(file.id)}
                    sx={{ p: 0 }}
                  >
                    <X size={16} color={theme.igIconColors.light} />
                  </IgIconButton>
                </Stack>
              );
            })}
          </Stack>
        )}
        <Stack sx={{ px: 3, pb: 3, pt: 2 }}>
          <IgButton
            fullWidth
            variant="outlined"
            disabled={disabled || !attachments}
            onClick={
              picked.length > 0 ? onSubmit : () => attachments?.openFileDialog()
            }
            sx={{ height: 36 }}
          >
            {picked.length > 0 ? "Send to chat" : "Choose file"}
          </IgButton>
        </Stack>
      </SurveyCardShell>
    );
  }

  if (!questions || !question) {
    return null;
  }

  const total = questions.length;
  const multiSelect = question.multiSelect === true;
  const showStepper = total > 1 && survey !== null;
  const showBack = currentIndex > 0;
  const showForward =
    currentIndex < total - 1 && Boolean(survey?.answers[currentIndex + 1]);

  // Stepping back shows the row that was picked as pressed.
  const answeredValues = new Set(
    existingAnswer?.choices.map((choice) => choice.value) ?? []
  );

  // One answer path for every question kind: record it on the stepper when
  // there is one, else submit it as the single-question reply.
  const answer = (choices: UserInputAnswer["choices"]) => {
    if (survey) {
      survey.answerCurrent({ prompt: question.prompt, choices });
      return;
    }
    submitInput({
      type: "answers",
      answers: [{ prompt: question.prompt, choices }],
    });
  };

  const submitSingle = (option: Option) =>
    answer([{ value: option.value, label: option.label }]);

  const submitSelection = () => {
    if (isShopifyPickQuestion(question)) {
      return;
    }
    // Keep the agent's own option order so two users picking the same set send
    // the same reply.
    const chosen = question.options
      .filter((option) => selected.includes(option.value))
      .map((option) => ({ value: option.value, label: option.label }));
    if (chosen.length === 0) {
      return;
    }
    answer(chosen);
  };

  const submitPick = (picked: ShopifyEntityPick[]) => {
    setPickerOpen(() => false);
    if (picked.length === 0) {
      return;
    }
    answer(picked.map((item) => ({ value: item.id, label: item.label })));
  };

  const toggleOption = (value: string) =>
    setSelected((current) =>
      current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value]
    );

  const isRowSelected = (option: Option) =>
    multiSelect
      ? selected.includes(option.value)
      : answeredValues.has(option.value);

  const dismiss = () => {
    if (survey) {
      survey.dismiss();
      return;
    }
    declineAsk();
  };

  const stepper = showStepper ? (
    <Stack direction="row" alignItems="center" gap={1}>
      {/* Hidden arrows stay in the layout so the stepper's width never
          changes and the header doesn't shift as they come and go. */}
      <IgIconButton
        aria-label="Previous question"
        size="small"
        disabled={disabled}
        onClick={() => survey.goTo(currentIndex - 1)}
        sx={{ p: 0, visibility: showBack ? "visible" : "hidden" }}
      >
        <ChevronLeft size={16} color={theme.igIconColors.light} />
      </IgIconButton>
      <IgTypography
        variant="body3"
        sx={{ color: "text.tertiary", whiteSpace: "nowrap" }}
      >
        {currentIndex + 1} of {total}
      </IgTypography>
      <IgIconButton
        aria-label="Next question"
        size="small"
        disabled={disabled}
        onClick={() => survey.goTo(currentIndex + 1)}
        sx={{ p: 0, visibility: showForward ? "visible" : "hidden" }}
      >
        <ChevronRight size={16} color={theme.igIconColors.light} />
      </IgIconButton>
    </Stack>
  ) : null;

  if (isShopifyPickQuestion(question)) {
    const picked = existingAnswer?.choices ?? [];

    // Every flat catalog kind is picked inline, in the card, from the Figma
    // chat combobox. Only products keep a dialog: the shared picker's images,
    // variants and filters have no inline equivalent.
    if (question.entity !== "product") {
      return (
        <SurveyCardShell
          title={surveyCardTitle(question.prompt)}
          stepper={stepper}
          disabled={disabled}
          hasFooter
          onDismiss={dismiss}
        >
          <ShopifyEntityCombobox
            // Remounted per question so the running selection is always the
            // one this question recorded, never the previous question's.
            key={currentIndex}
            entity={question.entity}
            multiSelect={multiSelect}
            disabled={disabled}
            initialSelected={picked.map((item) => ({
              id: item.value,
              label: item.label,
            }))}
            onConfirm={submitPick}
          />
        </SurveyCardShell>
      );
    }

    return (
      <SurveyCardShell
        title={surveyCardTitle(question.prompt)}
        stepper={stepper}
        disabled={disabled}
        hasFooter
        onDismiss={dismiss}
      >
        {picked.length > 0 && (
          <Stack
            component="ul"
            data-testid="survey-card-picked"
            sx={{ listStyle: "none", m: 0, px: 3, py: 0 }}
          >
            {picked.map((item) => (
              <IgTypography
                key={item.value}
                component="li"
                variant="body2"
                noWrap
                sx={{ py: 1 }}
              >
                {item.label}
              </IgTypography>
            ))}
          </Stack>
        )}
        <Stack sx={{ px: 3, pb: 3, pt: 2 }}>
          <IgButton
            fullWidth
            variant="outlined"
            disabled={disabled}
            onClick={() => setPickerOpen(() => true)}
            sx={{ height: 36 }}
          >
            {picked.length > 0
              ? "Change selection"
              : shopifyEntityChooseLabel(question.entity, multiSelect)}
          </IgButton>
        </Stack>
        <ShopifyProductPickerDialog
          multiSelect={multiSelect}
          open={pickerOpen}
          saveDisabled={disabled}
          onClose={() => setPickerOpen(() => false)}
          onSave={submitPick}
        />
      </SurveyCardShell>
    );
  }

  return (
    <SurveyCardShell
      title={surveyCardTitle(question.prompt)}
      stepper={stepper}
      disabled={disabled}
      hasFooter={multiSelect}
      onDismiss={dismiss}
    >
      <Stack sx={{ px: 1.5 }}>
        {question.options.map((option, index) => {
          const pressed = isRowSelected(option);
          const label = (
            <Stack gap={1} sx={{ minWidth: 0 }}>
              <IgTypography variant="body2">{option.label}</IgTypography>
              {option.description && (
                <IgTypography variant="body3" sx={{ color: "text.secondary" }}>
                  {option.description}
                </IgTypography>
              )}
            </Stack>
          );

          if (multiSelect) {
            return (
              <Stack
                key={option.value}
                component="label"
                direction="row"
                gap={2}
                sx={{
                  alignItems: "center",
                  borderRadius: "6px",
                  cursor: disabled ? "default" : "pointer",
                  px: 2,
                  py: 2,
                  "&:hover": {
                    backgroundColor: disabled
                      ? "transparent"
                      : theme.igBackgroundColors.interactive.neutral.hover,
                  },
                }}
              >
                <IgCheckbox
                  checked={pressed}
                  disabled={disabled}
                  onChange={() => toggleOption(option.value)}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    p: 0,
                    // The theme pins every checkbox to a 32px hit box with
                    // `!important`; Figma's survey row wants the 16px control
                    // itself, so the row keeps its 8px padding instead of
                    // inheriting an extra 8px of checkbox chrome.
                    "&.MuiButtonBase-root.MuiCheckbox-root": {
                      height: "16px !important",
                      width: "16px !important",
                    },
                    "& .MuiSvgIcon-root": { fontSize: 16 },
                  }}
                />
                {label}
              </Stack>
            );
          }

          return (
            <ButtonBase
              key={option.value}
              disabled={disabled}
              onClick={() => submitSingle(option)}
              sx={{
                alignItems: "flex-start",
                // Figma's pressed fill; the semantic `interactive.neutral.active`
                // token is a different gray, so the primitive is the faithful
                // mapping.
                backgroundColor: pressed
                  ? IgGrayPrimitives[400]
                  : "transparent",
                borderRadius: "6px",
                display: "flex",
                gap: 2,
                justifyContent: "flex-start",
                px: 2,
                py: 2,
                textAlign: "left",
                width: "100%",
                "&:hover": {
                  backgroundColor: pressed
                    ? IgGrayPrimitives[400]
                    : theme.igBackgroundColors.interactive.neutral.hover,
                },
                "&:active": { backgroundColor: IgGrayPrimitives[400] },
              }}
            >
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  backgroundColor: IgGrayPrimitives[500],
                  borderRadius: "6px",
                  color: "text.tertiary",
                  flexShrink: 0,
                  fontSize: 12,
                  height: 20,
                  width: 20,
                }}
              >
                {index + 1}
              </Stack>
              {label}
            </ButtonBase>
          );
        })}
      </Stack>

      {multiSelect && (
        <Stack sx={{ px: 3, pb: 3, pt: 2 }}>
          <IgButton
            fullWidth
            variant="outlined"
            disabled={disabled || selected.length === 0}
            onClick={submitSelection}
            sx={{ height: 36 }}
          >
            Confirm selections
          </IgButton>
        </Stack>
      )}
    </SurveyCardShell>
  );
};

/**
 * The card chrome every ask shares: the 12px-radius low-shadow surface and
 * the 12/12/8-padded header holding the title, an optional stepper, and the
 * dismiss X.
 * The controls sit centred on the title's first line whether it wraps or not.
 */
const SurveyCardShell = ({
  title,
  stepper,
  disabled,
  hasFooter = false,
  onDismiss,
  children,
}: {
  title: string;
  stepper?: ReactNode;
  disabled: boolean;
  /** A footer brings its own 12px bottom padding, so the card adds none. */
  hasFooter?: boolean;
  onDismiss: () => void;
  children: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Stack
      data-testid="survey-card"
      sx={{
        backgroundColor: theme.igBackgroundColors.baseLightHigh,
        border: `1px solid ${theme.igBorders.core.default}`,
        borderRadius: "12px",
        boxShadow: theme.igShadows.low,
        marginX: 1,
        overflow: "hidden",
        paddingBottom: hasFooter ? 0 : 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        sx={{ px: 3, pt: 3, pb: 2 }}
      >
        <IgTypography
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: 600,
            minWidth: 0,
            whiteSpace: "pre-line",
          }}
        >
          {title}
        </IgTypography>
        {/* As tall as one body1 line, so the controls sit centred on the
            prompt's first line whether the question wraps or not. */}
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          flexShrink={0}
          sx={{ height: theme.typography.body2.lineHeight }}
        >
          {stepper}
          <IgIconButton
            aria-label="Dismiss question"
            size="small"
            disabled={disabled}
            onClick={onDismiss}
            sx={{ p: 0 }}
          >
            <X size={16} color={theme.igIconColors.light} />
          </IgIconButton>
        </Stack>
      </Stack>
      {children}
    </Stack>
  );
};
